import React, { useEffect } from 'react';
import { ArrowRight, Video, Users, Calendar, HelpCircle, CheckCircle2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import gsap from 'gsap';

const WinningWednesdayPage = () => {
    useEffect(() => {
        window.scrollTo(0, 0);

        const ctx = gsap.context(() => {
            gsap.fromTo('.stagger-fade',
                { y: 30, opacity: 0 },
                {
                    y: 0,
                    opacity: 1,
                    duration: 0.8,
                    stagger: 0.15,
                    ease: 'power3.out'
                }
            );
        });

        return () => ctx.revert();
    }, []);

    const zoomLink = "https://roofingbusinesspartner.zoom.us/webinar/register/WN_32DcfJ0ATsKwSmj9Dpmwpw#/registration";

    return (
        <div className="min-h-screen bg-[#0A0A14] pt-32 pb-24 relative selection:bg-[#7B61FF] selection:text-[#F0EFF4]">
            {/* Background elements */}
            <div className="absolute top-0 left-0 w-full h-[60vh] bg-gradient-to-b from-[#18181B] to-transparent bg-opacity-50 pointer-events-none"></div>
            <div className="absolute top-[20%] right-[10%] w-[400px] h-[400px] bg-[#7B61FF] opacity-10 blur-[150px] rounded-full mix-blend-screen pointer-events-none"></div>

            <div className="max-w-5xl mx-auto px-6 relative z-10">
                {/* Hero Section */}
                <div className="text-center mb-20 stagger-fade">
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#18181B] border border-[#7B61FF]/30 text-[#7B61FF] text-sm font-data mb-6 font-semibold shadow-[0_0_15px_rgba(123,97,255,0.15)]">
                        <Video className="w-4 h-4" />
                        Live Support Sessions
                    </div>
                    <h1 className="text-5xl md:text-7xl font-bold font-heading tracking-tight mb-6 leading-tight">
                        Welcome to <br />
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#7B61FF] to-[#A78BFA] font-drama italic pr-4">Winning Wednesday.</span>
                    </h1>
                    <p className="text-[#F0EFF4]/70 text-lg md:text-2xl leading-relaxed max-w-3xl mx-auto font-light mb-12">
                        Your free, weekly live support webinar. Bring your questions, hit roadblocks, or just listen in. We stay until every single operator's question is answered.
                    </p>

                    <a href={zoomLink} target="_blank" rel="noopener noreferrer" className="group relative inline-flex items-center justify-center gap-3 px-8 py-4 bg-[#7B61FF] text-white rounded-full font-bold text-lg overflow-hidden transition-all duration-300 hover:scale-105 hover:shadow-[0_0_30px_rgba(123,97,255,0.4)]">
                        <span className="relative z-10 flex items-center gap-2">Register for the Next Session <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" /></span>
                        <div className="absolute inset-0 bg-gradient-to-r from-[#7B61FF] to-[#5a42d6] opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    </a>
                    <p className="text-sm text-[#F0EFF4]/40 mt-4">100% Free. Every single week. Register once, attend when you want.</p>
                </div>

                {/* Features Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-20 stagger-fade">
                    <div className="bg-[#18181B] border border-[#F0EFF4]/10 rounded-[2rem] p-8 hover:border-[#7B61FF]/30 transition-colors duration-300 group">
                        <div className="w-12 h-12 rounded-full bg-[#7B61FF]/10 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                            <HelpCircle className="w-6 h-6 text-[#7B61FF]" />
                        </div>
                        <h3 className="text-2xl font-bold font-heading mb-4">Unblock Your Build</h3>
                        <p className="text-[#F0EFF4]/60 leading-relaxed">
                            Stuck on a workflow? Integrations not firing properly? Jump on the call. We will share screens, troubleshoot live, and get your RootClaw node operating at absolute peak efficiency.
                        </p>
                    </div>

                    <div className="bg-[#18181B] border border-[#F0EFF4]/10 rounded-[2rem] p-8 hover:border-[#7B61FF]/30 transition-colors duration-300 group">
                        <div className="w-12 h-12 rounded-full bg-[#7B61FF]/10 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                            <Users className="w-6 h-6 text-[#7B61FF]" />
                        </div>
                        <h3 className="text-2xl font-bold font-heading mb-4">Voice of the Operator</h3>
                        <p className="text-[#F0EFF4]/60 leading-relaxed">
                            This is not just tech support; it's product evolution. We listen to the roofing operators on these calls to understand real-world edge cases. Your feedback actively shapes the future of RoofClaw updates.
                        </p>
                    </div>
                </div>

                {/* How it works */}
                <div className="bg-[#18181B] border border-[#F0EFF4]/10 rounded-[3rem] p-10 md:p-16 mb-20 relative overflow-hidden stagger-fade">
                    <div className="absolute top-0 right-0 w-full h-full bg-[radial-gradient(circle_at_top_right,rgba(123,97,255,0.05),transparent_50%)] pointer-events-none"></div>

                    <h2 className="text-3xl md:text-4xl font-bold font-heading mb-10 text-center">How It Works</h2>

                    <div className="space-y-8 max-w-2xl mx-auto">
                        <div className="flex gap-4">
                            <div className="flex-shrink-0 mt-1">
                                <CheckCircle2 className="w-6 h-6 text-[#7B61FF]" />
                            </div>
                            <div>
                                <h4 className="text-xl font-bold mb-2">1. Register Once</h4>
                                <p className="text-[#F0EFF4]/60">Use the link above to register for the Zoom webinar. Once you are in the system, you can choose to join any upcoming Wednesday session without re-registering.</p>
                            </div>
                        </div>

                        <div className="flex gap-4">
                            <div className="flex-shrink-0 mt-1">
                                <CheckCircle2 className="w-6 h-6 text-[#10B981]" />
                            </div>
                            <div>
                                <h4 className="text-xl font-bold mb-2">2. Drop In When You Need It</h4>
                                <p className="text-[#F0EFF4]/60">You do not have to attend every week. Only show up when you have a question, want to learn from others' questions, or need strategic guidance.</p>
                            </div>
                        </div>

                        <div className="flex gap-4">
                            <div className="flex-shrink-0 mt-1">
                                <CheckCircle2 className="w-6 h-6 text-[#7B61FF]" />
                            </div>
                            <div>
                                <h4 className="text-xl font-bold mb-2">3. No Question Left Behind</h4>
                                <p className="text-[#F0EFF4]/60">We schedule these for an hour, but the rule is simple: we stay on the line until every single person's question has been addressed safely and effectively.</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Final CTA */}
                <div className="text-center stagger-fade">
                    <h2 className="text-3xl font-heading font-bold mb-6">Ready to join the community?</h2>
                    <a href={zoomLink} target="_blank" rel="noopener noreferrer" className="group relative inline-flex items-center justify-center gap-3 px-8 py-4 bg-[#F0EFF4] text-[#0A0A14] rounded-full font-bold text-lg overflow-hidden transition-transform duration-300 hover:scale-105">
                        <span className="relative z-10 flex items-center gap-2">Secure Your Spot <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" /></span>
                        <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    </a>
                </div>
            </div>
        </div>
    );
};

export default WinningWednesdayPage;
